import { type Deployment, QUOTAS } from '@xb/shared';
import { type DragEvent, useCallback, useEffect, useId, useRef, useState } from 'react';
import { api, errorMessage, type LogEvent } from '../api.js';
import { useResource } from '../hooks.js';
import { useApp } from '../pages/AppPage.js';
import { fmtBytes, fmtDateTime, fmtTime } from './format.js';
import { ErrorNotice, Loading, Notice } from './Notice.js';
import { StatusPill } from './StatusPill.js';

type Phase = 'idle' | 'uploading' | 'starting' | 'watching' | 'done' | 'failed';

const inFlight = (d: Deployment) =>
  d.status === 'uploaded' || d.status === 'building' || d.status === 'releasing';

export function DeployTab() {
  const { app, reload: reloadApp } = useApp();
  const history = useResource(() => api.listDeployments(app.slug), [app.slug]);
  const reloadHistory = history.reload;

  const [phase, setPhase] = useState<Phase>('idle');
  const [watchId, setWatchId] = useState<string | undefined>(undefined);
  const [current, setCurrent] = useState<Deployment | undefined>(undefined);
  const [lines, setLines] = useState<LogEvent[]>([]);
  const [error, setError] = useState<unknown>(undefined);
  const [over, setOver] = useState(false);
  const fileInputId = useId();
  const logRef = useRef<HTMLPreElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [failedAt, setFailedAt] = useState<Phase | undefined>(undefined);

  // If a build is already running (another tab, the CLI, a refresh), follow it.
  useEffect(() => {
    if (phase !== 'idle' || !history.data) return;
    const running = history.data.deployments.find((d) => d.status === 'building' || d.status === 'releasing');
    if (running) {
      setCurrent(running);
      setLines([]);
      setWatchId(running.id);
      setPhase('watching');
    }
  }, [history.data, phase]);

  // Poll the deployment and its build log every 3s until it settles.
  useEffect(() => {
    if (!watchId) return;
    let cancelled = false;
    let next: string | undefined;
    let timer: number | undefined;

    const tick = async () => {
      try {
        const [{ deployment }, logs] = await Promise.all([
          api.getDeployment(app.slug, watchId),
          api.deploymentLogs(app.slug, watchId, next),
        ]);
        if (cancelled) return;
        if (logs.events.length) setLines((prev) => [...prev, ...logs.events]);
        if (logs.next) next = logs.next;
        setCurrent(deployment);
        if (deployment.status === 'live' || deployment.status === 'failed') {
          if (deployment.status === 'failed') setFailedAt('watching');
          setPhase(deployment.status === 'live' ? 'done' : 'failed');
          setWatchId(undefined);
          reloadHistory();
          reloadApp();
          return;
        }
      } catch (e) {
        if (cancelled) return;
        // Transient errors shouldn't end the watch; a 401 or 404 should.
        const status = (e as { status?: number }).status;
        if (status === 401 || status === 404) {
          setError(e);
          setFailedAt('watching');
          setPhase('failed');
          setWatchId(undefined);
          return;
        }
      }
      timer = window.setTimeout(() => void tick(), 3000);
    };
    void tick();
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [watchId, app.slug, reloadHistory, reloadApp]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: scroll to the bottom whenever new lines arrive
  useEffect(() => {
    const el = logRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [lines]);

  const busy = phase === 'uploading' || phase === 'starting' || phase === 'watching';

  const deploy = useCallback(
    async (file: File) => {
      setError(undefined);
      if (!/\.zip$/i.test(file.name)) {
        setError(new Error('Please choose a .zip file.'));
        return;
      }
      if (file.size > QUOTAS.uploadMaxBytes) {
        setError(
          new Error(
            `That zip is ${fmtBytes(file.size)}; the limit is ${fmtBytes(QUOTAS.uploadMaxBytes)}. Leave out node_modules and build output.`,
          ),
        );
        return;
      }
      if (file.size === 0) {
        setError(new Error('That file is empty.'));
        return;
      }
      setLines([]);
      setCurrent(undefined);
      setFailedAt(undefined);
      let stage: Phase = 'uploading';
      try {
        setPhase(stage);
        const { deployment, uploadUrl } = await api.createDeployment(app.slug, file.size);
        setCurrent(deployment);
        await api.uploadZip(uploadUrl, file);
        stage = 'starting';
        setPhase(stage);
        const started = await api.startDeployment(app.slug, deployment.id);
        setCurrent(started.deployment);
        stage = 'watching';
        setPhase(stage);
        setWatchId(deployment.id);
      } catch (e) {
        setError(e);
        setFailedAt(stage);
        setPhase('failed');
        reloadHistory();
      }
    },
    [app.slug, reloadHistory],
  );

  const onDrop = (e: DragEvent<HTMLFieldSetElement>) => {
    e.preventDefault();
    setOver(false);
    if (busy) return;
    const file = e.dataTransfer.files[0];
    if (file) void deploy(file);
  };

  const cannotDeploy = app.status === 'deleting' || app.status === 'failed';
  const ORDER: Phase[] = ['uploading', 'starting', 'watching', 'done'];
  const stepState = (step: Phase): 'todo' | 'active' | 'done' | 'failed' => {
    if (phase === 'idle') return 'todo';
    const mine = ORDER.indexOf(step);
    const now = ORDER.indexOf(phase === 'failed' ? (failedAt ?? 'uploading') : phase);
    if (mine < now) return 'done';
    if (mine > now) return 'todo';
    if (phase === 'failed') return 'failed';
    return phase === 'done' ? 'done' : 'active';
  };

  return (
    <>
      {cannotDeploy ? (
        <Notice kind="warn">
          This app is {app.status === 'failed' ? 'in a failed state' : 'being deleted'}, so nothing can be
          deployed to it.
        </Notice>
      ) : null}

      <section className="card">
        <h2>Upload a new version</h2>
        <p className="small subtle">
          Zip your project folder and drop it here. Leave out <code>node_modules</code>, <code>.git</code> and{' '}
          <code>.env</code> files — the build installs what it needs.{' '}
          {app.type === 'static'
            ? 'A folder with an index.html, or a project with a build script, both work.'
            : 'A package.json, requirements.txt or Dockerfile tells the build how to run it.'}
        </p>
        <fieldset
          className={`dropzone ${over ? 'over' : ''}`}
          onDragOver={(e) => {
            e.preventDefault();
            if (!busy) setOver(true);
          }}
          onDragLeave={() => setOver(false)}
          onDrop={onDrop}
        >
          <legend className="visually-hidden">Upload a zip file</legend>
          <p>{busy ? 'Working on the current upload…' : 'Drag a .zip here, or'}</p>
          <button
            type="button"
            className="btn btn-primary"
            disabled={busy || cannotDeploy}
            onClick={() => fileRef.current?.click()}
          >
            Choose a zip file
          </button>
          <label htmlFor={fileInputId} className="visually-hidden">
            Zip file
          </label>
          <input
            id={fileInputId}
            ref={fileRef}
            className="visually-hidden"
            type="file"
            tabIndex={-1}
            accept=".zip,application/zip,application/x-zip-compressed"
            disabled={busy || cannotDeploy}
            onChange={(e) => {
              const f = e.target.files?.[0];
              e.target.value = '';
              if (f) void deploy(f);
            }}
          />
          <p className="small subtle" style={{ marginTop: '0.75rem', marginBottom: 0 }}>
            Up to {fmtBytes(QUOTAS.uploadMaxBytes)}. {QUOTAS.deploysPerAppPerDay} uploads a day per app.
          </p>
        </fieldset>

        {phase !== 'idle' ? (
          <>
            <ol className="steps" aria-label="Deployment progress">
              <li data-state={stepState('uploading')}>1. Upload</li>
              <li data-state={stepState('starting')}>2. Start build</li>
              <li data-state={stepState('watching')}>3. Build and release</li>
              <li data-state={stepState('done')}>4. Live</li>
            </ol>
            {current ? (
              <p className="small" role="status">
                Deployment <span className="mono">{current.id}</span> — <StatusPill status={current.status} />
                {current.error ? <span className="subtle"> — {current.error}</span> : null}
              </p>
            ) : null}
            {phase === 'done' ? (
              <Notice kind="ok">
                Your new version is live
                {app.status === 'provisioning' ? ' and will be served as soon as the address is ready' : ''}.{' '}
                {app.status === 'active' ? (
                  <a href={`https://${app.host}`} target="_blank" rel="noreferrer">
                    Open it ↗
                  </a>
                ) : null}
              </Notice>
            ) : null}
            {phase === 'failed' && !error ? (
              <Notice kind="warn">
                The build didn't finish. Check the log below for the reason, fix it, and upload again.
              </Notice>
            ) : null}
          </>
        ) : null}
        <ErrorNotice error={error} />
        {phase !== 'idle' ? (
          <>
            <h3>Build log</h3>
            <pre className="log" ref={logRef} role="log" aria-label="Build log">
              {lines.map((l) => `${fmtTime(l.at)}  ${l.message.replace(/\n$/, '')}\n`).join('')}
            </pre>
          </>
        ) : null}
      </section>

      <section className="card">
        <h2>History</h2>
        <ErrorNotice error={history.error} />
        {history.loading && !history.data ? <Loading /> : null}
        {history.data && history.data.deployments.length === 0 ? (
          <p className="subtle">Nothing has been deployed yet.</p>
        ) : null}
        {history.data && history.data.deployments.length > 0 ? (
          <ul className="rows">
            {history.data.deployments.map((d) => (
              <li key={d.id}>
                <div>
                  <div className="primary mono">
                    {d.id}
                    {d.id === app.currentDeployId ? (
                      <span className="badge" style={{ marginLeft: '0.5rem' }}>
                        Current
                      </span>
                    ) : null}
                  </div>
                  <div className="secondary">
                    {fmtDateTime(d.createdAt)}
                    {d.error ? ` — ${d.error}` : ''}
                  </div>
                </div>
                <div className="btn-row">
                  <StatusPill status={d.status} />
                  {inFlight(d) && d.status !== 'uploaded' && !busy ? (
                    <button
                      type="button"
                      className="btn btn-sm"
                      onClick={() => {
                        setError(undefined);
                        setLines([]);
                        setCurrent(d);
                        setWatchId(d.id);
                        setPhase('watching');
                      }}
                    >
                      Follow
                    </button>
                  ) : null}
                  {(d.status === 'live' || d.status === 'failed') && d.logGroup ? (
                    <button
                      type="button"
                      className="btn btn-sm"
                      disabled={busy}
                      onClick={async () => {
                        setError(undefined);
                        setCurrent(d);
                        setFailedAt('watching');
                        setPhase(d.status === 'live' ? 'done' : 'failed');
                        try {
                          const all: LogEvent[] = [];
                          let next: string | undefined;
                          for (let i = 0; i < 20; i++) {
                            const page = await api.deploymentLogs(app.slug, d.id, next);
                            all.push(...page.events);
                            if (!page.next || page.next === next || page.events.length === 0) break;
                            next = page.next;
                          }
                          setLines(all);
                        } catch (e) {
                          setError(new Error(`Could not load the log: ${errorMessage(e)}`));
                        }
                      }}
                    >
                      View log
                    </button>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        ) : null}
      </section>
    </>
  );
}
